/* global previews */
import { fabric } from "fabric";
import { CUSTOM_TEXT_BOX_CLASS_NAME, CustomTextBox } from './nodes/custom-text-box';
import { initOvalShadowBehind, initOvalShadowInFront} from "@frontend/oval-shadow";
import { createMirror } from "@frontend/mirror-shadow";
import {
  ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  IMAGE_PREFIX_ID, MULTI_LINE_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import {BEHIND_DISPLAY_MODE, IN_FRONT_DISPLAY_MODE} from "@frontend/constants/oval-shadows";
import {createCanvasResizeControl} from "@frontend/canvas-resize/create-canvas-resize-controler";
import {Effects} from "@frontend/lib/fabric/effects/Effects";
import {
  initSkewControls,
  initWarpControls,
} from "@frontend/lib/fabric/effects/fx";
import {
  initProjectedShadowControls,
  ProjectedShadowEffect,
} from "@frontend/lib/fabric/shadows/effects/fx/ProjectedShadow";
import { editModeService } from "@frontend/services/EditModeService";
import _ from "lodash";
import { RenderLock } from "@frontend/lib/fabric/render-lock";
// import rotateControl from "@frontend/lib/fabric-rotate-control";

// This function breaks the text editor. Text editor can not edit text for the second time.
// rotateControl()

fabric.Object.prototype.locked = false

const originalGetLineLeftOffset = fabric.Text.prototype._getLineLeftOffset;
const originalGetCursorBoundaries = fabric.Textbox.prototype._getCursorBoundaries;

Object.defineProperty(fabric.Object.prototype, 'locked', {
  get: function () {
    return this._locked || false
  },
  set: function (value) {
    this._locked = value
    this.lockMovementX = value
    this.lockMovementY = value
    this.lockScalingX = value
    this.lockScalingY = value
    this.lockRotation = value
    this.lockUniScaling = value
    this.editable = !value
  },
})

/**
 * Edit Mode Restricted Property
 * Returns true if the element is restricted based on the current edit mode.
 * This considers both individual lock state and edit mode restrictions.
 */
Object.defineProperty(fabric.Object.prototype, 'editModeRestricted', {
  get: function () {
    // If individually locked, always restricted
    if (this._locked) {
      return true;
    }
    // Check edit mode restrictions
    return !editModeService.canEditElement(this);
  },
})

/**
 * Get edit mode restrictions for this element
 * Returns detailed information about what can be edited
 */
fabric.Object.prototype.getEditModeRestrictions = function() {
  return editModeService.getElementRestrictions(this);
}

/**
 * Edit Mode Cursor Handling
 * Override the canvas cursor logic to show 'not-allowed' for restricted elements
 */
const NOT_ALLOWED_CURSOR = 'not-allowed';
const DEFAULT_HOVER_CURSOR = 'move';

// Store original _setCursorFromEvent
const originalSetCursorFromEvent = fabric.Canvas.prototype._setCursorFromEvent;

fabric.Canvas.prototype._setCursorFromEvent = function(e, target) {
  // If there's a target and edit mode is restricted for it, show not-allowed cursor
  if (target && editModeService.isRestricted()) {
    // Only apply cursor restrictions if the element has standard identifying properties
    // PSD-imported elements without element_id will use their lock state for visual feedback
    const hasStandardId = target.element_id ||
      (target.id && (
        target.id.startsWith('image_') ||
        target.id.startsWith('text_') ||
        target.id.startsWith('multi_line_') ||
        target.id.startsWith('bg_color_') ||
        target.id.startsWith('bk_img_')
      ));

    if (hasStandardId) {
      const canEdit = editModeService.canEditElement(target);
      if (!canEdit) {
        this.setCursor(NOT_ALLOWED_CURSOR);
        return;
      }
    } else {
      // For elements without standard IDs (like PSD imports),
      // check if they are locked - if locked, show not-allowed cursor
      if (target.lockMovementX && target.lockMovementY) {
        this.setCursor(NOT_ALLOWED_CURSOR);
        return;
      }
    }
  }

  // Otherwise, use original logic
  return originalSetCursorFromEvent.call(this, e, target);
};

/**
 * Apply Edit Mode Restrictions to Canvas Objects
 * Call this function after canvas is loaded to lock/unlock elements based on edit mode
 */
export function applyEditModeRestrictionsToCanvas(canvas) {
  if (!canvas || !editModeService.isRestricted()) {
    return;
  }

  const objects = canvas.getObjects();
  objects.forEach(obj => {
    applyEditModeRestrictionToObject(obj);
  });

  canvas.requestRenderAll();
}

/**
 * Apply Edit Mode Restriction to a single object
 */
export function applyEditModeRestrictionToObject(obj) {
  if (!obj || !editModeService.isRestricted()) {
    return;
  }

  const canEdit = editModeService.canEditElement(obj);

  // For elements without standard IDs (PSD imports),
  // check if this looks like a product image based on naming
  const isPsdImport = !obj.element_id && obj.id && obj.id.startsWith('group_');
  let shouldBeEditable = canEdit;

  if (isPsdImport) {
    // For PSD imports, default to locked
    // Only unlock if explicitly marked as product or has certain characteristics
    shouldBeEditable = obj.isProductImage === true || obj.editableInRestrictedMode === true;
  }

  if (!shouldBeEditable) {
    obj.lockMovementX = true;
    obj.lockMovementY = true;
    obj.lockScalingX = true;
    obj.lockScalingY = true;
    obj.lockRotation = true;
    obj.hasControls = false;
    obj.hoverCursor = NOT_ALLOWED_CURSOR;
  }
}

/**
 * Mark an object as a product image (editable in all modes)
 */
export function markAsProductImage(obj) {
  if (obj) {
    obj.isProductImage = true;
    obj.editableInRestrictedMode = true;
  }
}

fabric.Object.prototype.drawCacheOnCanvas = function(ctx) {
  ctx.scale(1 / this.zoomX, 1 / this.zoomY);
  if (this._cacheCanvas.width > 0 && this._cacheCanvas.height > 0) {
    ctx.drawImage(this._cacheCanvas, -this.cacheTranslationX, -this.cacheTranslationY);
  }
}

fabric.Text.prototype.getLineSpacing = function (lineIndex) {
  let lineSpacing = this.lineHeight;
  const prevLineIndex = lineIndex - 1;

  if (prevLineIndex >= 0) {
    lineSpacing = this.getValueOfPropertyAt(prevLineIndex, 0, 'lineSpacing') ?? this.lineHeight;
  }
  return lineSpacing
}


function getJustifyInterCharSpacing(_this, lineIndex, overflowSpecialChars) {
  const line = _this._textLines[lineIndex];
  let totalTextWidth = 0;
  let overflowedCharWidth = 0;

  const overflowableChars = [`,`, `.`, `"`, `'`, '`'];
  for (var i = 0; i < line.length; i++) {
    if (overflowSpecialChars && i === (line.length - 1) && overflowableChars.includes(line[i])) {
      overflowedCharWidth = _this.__charBounds[lineIndex][i].width;
    }
    totalTextWidth += _this.__charBounds[lineIndex][i].width;
  }

  let totalWidth = _this.width - totalTextWidth + overflowedCharWidth;
  return totalWidth / (line.length - 1);
}

fabric.IText.prototype.renderCursorOrSelection = function() {
  if (!this.isEditing || !this.canvas || !this.canvas.contextTop) {
    return;
  }
  var boundaries = this._getCursorBoundaries(),
      ctx = this.canvas.contextTop;
  this.clearContextTop(true);
  if (this.selectionStart === this.selectionEnd) {
    this.renderCursor(boundaries, ctx);
  }
  else {
    this.renderSelection(boundaries, ctx);
  }
  ctx.restore();
}

const originalOnInput = fabric.IText.prototype.onInput;
const originalExitEditing = fabric.IText.prototype.exitEditing;
const originalGetValueOfPropertyAt = fabric.IText.prototype.getValueOfPropertyAt;

function shouldRememberEmptyLineStyle(textObject) {
  const id = String(textObject.id || '');

  return id.startsWith(MULTI_LINE_PREFIX_ID)
    || id.startsWith(ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID);
}

function clearRememberedEmptyLineStyles(textObject) {
  delete textObject._emptyLineStyles;
}

function getFirstExplicitLineStyle(styles, lineIndex) {
  const lineStyles = styles?.[lineIndex];
  if (!lineStyles) return null;

  const firstStyledCharIndex = Object.keys(lineStyles)
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .find(charIndex => Object.keys(lineStyles[charIndex] || {}).length > 0);

  if (firstStyledCharIndex === undefined) return null;

  return fabric.util.object.clone(lineStyles[firstStyledCharIndex], true);
}

function getRememberedLineStyle(textObject, lineIndex) {
  const styleLineIndex = textObject._styleMap && !textObject.isWrapping
    ? textObject._styleMap[lineIndex]?.line
    : lineIndex;

  return textObject._emptyLineStyles?.[styleLineIndex];
}

function refreshTextDimensions(textObject) {
  textObject._forceClearCache = true;
  textObject.dirty = true;
  textObject.initDimensions();
  textObject.setCoords();
  textObject.canvas?.requestRenderAll();
}

// POR-14144: save cursor size while editing a multiline object
// use its temporary line style for cursor and line measurements instead
// of falling back to the text object's default font size.
fabric.IText.prototype.getValueOfPropertyAt = function(lineIndex, charIndex, property) {
  if (shouldRememberEmptyLineStyle(this)) {
    const charStyle = this._getStyleDeclaration(lineIndex, charIndex);
    const rememberedStyle = getRememberedLineStyle(this, lineIndex);

    if ((!charStyle || typeof charStyle[property] === 'undefined')
      && rememberedStyle && typeof rememberedStyle[property] !== 'undefined') {
      return rememberedStyle[property];
    }
  }

  return originalGetValueOfPropertyAt.call(this, lineIndex, charIndex, property);
};

// Fabric stores formatting per character. 
// POR-14144 Keep a temporary style for a line whose
// final character is deleted so typing on that still-empty line restores its format.
fabric.IText.prototype.onInput = function(...args) {
  if (!shouldRememberEmptyLineStyle(this)) {
    return originalOnInput.apply(this, args);
  }

  const previousLines = this.text.split('\n');
  const previousLineStyles = previousLines.map((line, lineIndex) => (
    line.length ? getFirstExplicitLineStyle(this.styles, lineIndex) : null
  ));
  const wasPaste = this.fromPaste;
  const result = originalOnInput.apply(this, args);
  const currentLines = this.text.split('\n');

  if (previousLines.length !== currentLines.length) {
    const hadRememberedStyles = Boolean(this._emptyLineStyles);
    clearRememberedEmptyLineStyles(this);
    if (hadRememberedStyles) refreshTextDimensions(this);
    return result;
  }

  let dimensionsNeedRefresh = false;

  currentLines.forEach((line, lineIndex) => {
    const previousLine = previousLines[lineIndex];
    const rememberedStyle = this._emptyLineStyles?.[lineIndex];

    if (previousLine.length > 0 && line.length === 0) {
      const previousStyle = previousLineStyles[lineIndex];
      if (previousStyle) {
        this._emptyLineStyles = this._emptyLineStyles || {};
        this._emptyLineStyles[lineIndex] = previousStyle;
        dimensionsNeedRefresh = true;
      }
      return;
    }

    if (previousLine.length === 0 && line.length > 0 && rememberedStyle) {
      this.styles = this.styles || {};
      this.styles[lineIndex] = this.styles[lineIndex] || {};

      for (let charIndex = 0; charIndex < line.length; charIndex++) {
        const hasPastedStyle = Object.keys(this.styles[lineIndex][charIndex] || {}).length > 0;
        if (!wasPaste || !hasPastedStyle) {
          this.styles[lineIndex][charIndex] = fabric.util.object.clone(rememberedStyle, true);
          dimensionsNeedRefresh = true;
        }
      }
      delete this._emptyLineStyles[lineIndex];
    }
  });

  if (this._emptyLineStyles && Object.keys(this._emptyLineStyles).length === 0) {
    clearRememberedEmptyLineStyles(this);
  }

  if (dimensionsNeedRefresh) refreshTextDimensions(this);

  return result;
};

fabric.IText.prototype.exitEditing = function(...args) {
  if (!shouldRememberEmptyLineStyle(this)) {
    return originalExitEditing.apply(this, args);
  }

  if (this._emptyLineStyles) {
    clearRememberedEmptyLineStyles(this);
    this._forceClearCache = true;
  }
  return originalExitEditing.apply(this, args);
};

fabric.IText.prototype._getCursorBoundariesOffsets = function(position) {
  if (this.cursorOffsetCache && 'top' in this.cursorOffsetCache) {
    return this.cursorOffsetCache;
  }
  var lineLeftOffset,
      lineIndex,
      charIndex,
      topOffset = 0,
      leftOffset = 0,
      boundaries,
      cursorPosition = this.get2DCursorLocation(position);
  charIndex = cursorPosition.charIndex;
  lineIndex = cursorPosition.lineIndex;
  for (var i = 0; i < lineIndex; i++) {
    topOffset += this.getHeightOfLine(i);
  }
  lineLeftOffset = this._getLineLeftOffset(lineIndex);
  var bound = this.__charBounds[lineIndex][charIndex];
  if (bound) {
    if (this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1) {
      const overflowSpecialChars = this.textAlign.indexOf('justify-inter-char-overflow') !== -1
      const charSpacing = getJustifyInterCharSpacing(this, lineIndex, overflowSpecialChars);
      leftOffset = bound.left + ((charIndex - 1) * charSpacing);
    } else {
      leftOffset = bound.left;
    }
  }
  if (this.charSpacing !== 0 && charIndex === this._textLines[lineIndex].length) {
    leftOffset -= this._getWidthOfCharSpacing();
  }
  boundaries = {
    top: topOffset,
    left: lineLeftOffset + (leftOffset > 0 ? leftOffset : 0),
  };
  this.cursorOffsetCache = boundaries;
  return this.cursorOffsetCache;
}

function getAlignments(textAlign) {
  const parts = (textAlign || '').split(',');
  return {
    horizontal: parts[0] || 'left',
    vertical: parts[1] || 'top'
  };
}

const originalEnlargeSpaces = fabric.Text.prototype.enlargeSpaces;

const convertJustifyToLeft = (textAlign) => {
  if (!textAlign) {
    return textAlign;
  }
  const parts = textAlign.split(',');
  if (parts[0] !== 'justify') {
    return textAlign;
  }
  parts[0] = 'justify-left';
  return parts.join(',');
};

fabric.Text.prototype.enlargeSpaces = function (...args) {
  const normalizedAlign = convertJustifyToLeft(this.textAlign);
  if (normalizedAlign === this.textAlign) {
    return originalEnlargeSpaces.apply(this, args);
  }

  const originalAlign = this.textAlign;
  this.textAlign = normalizedAlign;
  const result = originalEnlargeSpaces.apply(this, args);
  this.textAlign = originalAlign;
  return result;
};

fabric.Text.prototype._render = function(ctx) {
  const alignments = getAlignments(this.textAlign);

  // do not use char spacing in justify inter char mode
  const isJustifyInterChar = this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1;
  if (isJustifyInterChar) {
    this.charSpacing = 0;
  }

  const oldTextAlign = this.textAlign;
  this.textAlign = alignments.horizontal;

  if (this.fixedHeight){
    this.height = Math.max(this.fixedHeight, this.calcTextHeight());
  }

  if (this.verticalOffset !== undefined) {
    ctx.verticalOffset = this.verticalOffset;
    ctx.save();
    ctx.translate(0, this.verticalOffset);
  }
  const path = this.path;
  path && !path.isNotVisible() && path._render(ctx);
  this._setTextStyles(ctx);
  this._renderTextLinesBackground(ctx);
  this._renderTextDecoration(ctx, 'underline');
  this._renderText(ctx);
  this._renderTextDecoration(ctx, 'overline');
  this._renderTextDecoration(ctx, 'linethrough');

  if (this.verticalOffset !== undefined) {
    ctx.restore();
  }

  this.textAlign = oldTextAlign;
}

function calculateVerticalOffset(textObject, verticalAlign) {
  if (!textObject || !verticalAlign || verticalAlign === 'top') {
    return 0;
  }

  const totalHeight = textObject.calcTextHeight();
  const boxHeight = textObject.fixedHeight ? textObject.fixedHeight : textObject.height || totalHeight;
  const availableSpace = Math.max(0, boxHeight - totalHeight);

  if (verticalAlign === 'center' || verticalAlign === 'middle') {
    return availableSpace / 2;
  } else if (verticalAlign === 'bottom') {
    return availableSpace;
  }

  return 0;
}

fabric.Textbox.prototype._getCursorBoundaries = function(position, skipCaching) {
  const alignments = getAlignments(this.textAlign);
  const oldTextAlign = this.textAlign;
  const oldVerticalOffset = this.verticalOffset;

  this.textAlign = alignments.horizontal;
  try {
    this.verticalOffset = calculateVerticalOffset(this, alignments.vertical);
  } catch (error) {
    //Keep editing flow alive even if offset calculation fails unexpectedly.
    console.error('Failed to calculate vertical text offset', error);
    this.verticalOffset = 0;
  }

  const result = originalGetCursorBoundaries.call(this, position, skipCaching);

  if (this.verticalOffset) {
    result.top += this.verticalOffset;
  }

  this.textAlign = oldTextAlign;
  this.verticalOffset = oldVerticalOffset;
  
  return result;
};

fabric.Text.prototype._getLineLeftOffset = function(lineIndex) {
  const alignments = getAlignments(this.textAlign);
  const oldTextAlign = this.textAlign;
  this.textAlign = alignments.horizontal;

  const result = originalGetLineLeftOffset.call(this, lineIndex);

  this.textAlign = oldTextAlign;
  return result;
};

const getCursorPositionAfterChange = function (oldText, newText) {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');

  // Find the first line that differs
  let changedLineIndex = -1;
  for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
    if (oldLines[i] !== newLines[i]) {
      changedLineIndex = i;
      break;
    }
  }

  // If no changes found, return end of text
  if (changedLineIndex === -1) {
    return {
      lineIndex: newLines.length - 1,
      charIndex: newLines[newLines.length - 1].length
    };
  }

  const oldLine = oldLines[changedLineIndex] || '';
  const newLine = newLines[changedLineIndex] || '';

  // Find where the change ends (cursor position)
  // Start from the beginning and find first difference
  let charIndex = 0;
  for (let i = 0; i < newLine.length; i++) {
    if (oldLine[i] !== newLine[i]) {
      // Find the end of the inserted/changed text
      // This is where cursor should be
      charIndex = i;

      // If it's an insertion, find where old text continues
      for (let j = i; j < newLine.length; j++) {
        // Check if remaining new text matches old text from some offset
        let matchFound = false;
        for (let k = i; k <= oldLine.length; k++) {
          if (newLine.substring(j) === oldLine.substring(k)) {
            charIndex = j;
            matchFound = true;
            break;
          }
        }
        if (matchFound) break;
      }

      // If no match found, cursor is at end of new line
      if (charIndex === i) {
        charIndex = newLine.length;
      }

      break;
    }
  }

  // If we went through whole line without finding difference
  // cursor is at the end of changes
  if (charIndex === 0 && oldLine !== newLine) {
    charIndex = newLine.length;
  }

  return {
    lineIndex: changedLineIndex,
    charIndex: charIndex
  };
};

fabric.IText.prototype.updateTextAndPreserveCursor = function (oldText, newText) {
  if (!this.isEditing) {
    this.text = newText;
    // this.initDimensions();// when not commented - not remove from canvas deleted chars
    this.setCoords();
    return;
  }

  const { lineIndex, charIndex } = getCursorPositionAfterChange(oldText, newText);
  this.setSelectionByLine(lineIndex, charIndex);
};

//Helper to set cursor position by line and char index
fabric.IText.prototype.setSelectionByLine = function(lineIndex, charIndex) {
  let position = 0;
  const lines = this.text.split('\n');

  for (let i = 0; i < lineIndex && i < lines.length; i++) {
    position += lines[i].length + 1; // +1 for newline
  }

  position += Math.min(charIndex, lines[lineIndex]?.length || 0);

  this.selectionStart = position;
  this.selectionEnd = position;
  this.clearContextTop();
  this.renderCursorOrSelection();
};

fabric.Text.prototype._renderChars = function(method, ctx, line, left, top, lineIndex) {
  // set proper line offset
  var lineHeight = this.getHeightOfLine(lineIndex),
      isJustifyInterChar = this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1,
      isLastWrappingLine = this.isEndOfWrapping(lineIndex),
      isJustifyInterWord = this.textAlign.indexOf('justify') !== -1 && !isJustifyInterChar,
      shouldApplyWordJustify = isJustifyInterWord && !isLastWrappingLine,
      shouldApplyCharJustify = isJustifyInterChar && !isLastWrappingLine,
      overflowSpecialChars = this.textAlign.indexOf('justify-inter-char-overflow') !== -1,
      actualStyle,
      nextStyle,
      charsToRender = '',
      charBox,
      boxWidth = 0,
      timeToRender,
      shortCut = !shouldApplyWordJustify && !shouldApplyCharJustify && this.charSpacing === 0 && this.isEmptyStyles(lineIndex),
      charSpacing = this.charSpacing;

  ctx.save();
  top -= lineHeight * this._fontSizeFraction / this.getLineSpacing(lineIndex);

  if (shortCut) {
    // render all the line in one pass without checking
    this._renderChar(method, ctx, lineIndex, 0, line.join(''), left, top, lineHeight);
    ctx.restore();
    return;
  }

  if (shouldApplyCharJustify) {
    charSpacing = getJustifyInterCharSpacing(this, lineIndex, overflowSpecialChars);
  }

  for (var i = 0, len = line.length - 1; i <= len; i++) {
    timeToRender = i === len || charSpacing;
    charsToRender += line[i];
    charBox = this.__charBounds[lineIndex][i];

    if (boxWidth === 0) {
      left += charBox.kernedWidth - charBox.width;
      boxWidth += charBox.width;
    } else {
      boxWidth += charBox.kernedWidth;
    }

    if (shouldApplyWordJustify && !timeToRender) {
      if (this._reSpaceAndTab.test(line[i])) {
        timeToRender = true;
      }
    }

    if (!timeToRender) {
      // if we have charSpacing, we render char by char
      actualStyle = actualStyle || this.getCompleteStyleDeclaration(lineIndex, i);
      nextStyle = this.getCompleteStyleDeclaration(lineIndex, i + 1);
      timeToRender = this._hasStyleChanged(actualStyle, nextStyle);
    }

    if (timeToRender) {
      this._renderChar(method, ctx, lineIndex, i, charsToRender, left, top, lineHeight);
      charsToRender = '';
      actualStyle = nextStyle;
      left += shouldApplyCharJustify ? boxWidth + charSpacing : boxWidth;
      boxWidth = 0;
    }
  }

  ctx.restore();
}

fabric[CUSTOM_TEXT_BOX_CLASS_NAME] = CustomTextBox;

export const EXTRA_FEATURES = {
  TRANSFORM_IN_GROUP: 'EXTRA_TRANSFORM_IN_GROUP'
}

export const recalculateGroupPoint = (width, height, point, isToCanvas = true) => {
  const hw = width / 2
  const hh = height / 2
  return {
    x: point.x + hw * (isToCanvas ? 1 : -1),
    y: point.y + hh * (isToCanvas ? 1 : -1)
  }
}

fabric.Canvas.prototype._transformObject = function(e) {
  var pointer = this.getPointer(e),
      transform = this._currentTransform;

  transform.reset = false;
  transform.shiftKey = e.shiftKey;
  transform.altKey = e[this.centeredKey];

  const target = this.getActiveObject();

  if (target && target.group && (target.extraFeatures).includes(EXTRA_FEATURES.TRANSFORM_IN_GROUP) && !['drag'].includes(transform.action)) {
    const groupPoint = recalculateGroupPoint(target.group.width, target.group.height, pointer, false)
    pointer.x = groupPoint.x
    pointer.y = groupPoint.y

    target.group.dirty = true
  }

  this._performTransformAction(e, transform, pointer);
  transform.actionPerformed && this.requestRenderAll();
}

const propProcessor = (obj, callback) => {
  return Object.keys(obj)
      .map(key => ({ key, value: callback(obj[key]) }))
      .reduce((acc, { key, value }) => {
        acc[key] = value
        return acc
      }, {})}

const coordsRecalc = (_oCoords, width, height) => propProcessor(_oCoords, (point) => ({
  ...recalculateGroupPoint(width, height, point),
  corner: point.corner ? propProcessor(point.corner, (cPoint) => recalculateGroupPoint(width, height, cPoint)) : undefined,
  touchCorner: point.touchCorner ? propProcessor(point.touchCorner, (cPoint) => recalculateGroupPoint(width, height, cPoint)) : undefined,
}))

fabric.Object.prototype._findTargetCorner = function (...args) {
  if (this.extraFeatures.includes(EXTRA_FEATURES.TRANSFORM_IN_GROUP)) {
    return this._findTargetCornerWithGroup(...args)
  }

  return this._findTargetCornerOriginal(...args)
}

fabric.Object.prototype._findTargetCornerOriginal = function(pointer, forTouch) {
  // objects in group, anykind, are not self modificable,
  // must not return an hovered corner.
  if (!this.hasControls || this.group || (!this.canvas || this.canvas._activeObject !== this)) {
    return false;
  }

  var ex = pointer.x,
      ey = pointer.y,
      xPoints,
      lines, keys = Object.keys(this.oCoords),
      j = keys.length - 1, i;
  this.__corner = 0;

  // cycle in reverse order so we pick first the one on top
  for (; j >= 0; j--) {
    i = keys[j];
    if (!this.isControlVisible(i)) {
      continue;
    }

    lines = this._getImageLines(forTouch ? this.oCoords[i].touchCorner : this.oCoords[i].corner);
    // // debugging
    //
    // this.canvas.contextTop.fillRect(lines.bottomline.d.x, lines.bottomline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.bottomline.o.x, lines.bottomline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.leftline.d.x, lines.leftline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.leftline.o.x, lines.leftline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.topline.d.x, lines.topline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.topline.o.x, lines.topline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.rightline.d.x, lines.rightline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.rightline.o.x, lines.rightline.o.y, 2, 2);

    xPoints = this._findCrossPoints({ x: ex, y: ey }, lines);
    if (xPoints !== 0 && xPoints % 2 === 1) {
      this.__corner = i;
      return i;
    }
  }
  return false;
}

fabric.Object.prototype._findTargetCornerWithGroup = function(pointer, forTouch) {
  // objects anykind, are not self modificable,
  // must not return an hovered corner.
  if (!this.hasControls || (!this.canvas || this.canvas._activeObject !== this)) {
    return false;
  }

  const oCoords = coordsRecalc(this.oCoords, this.group.width, this.group.height)

  var ex = pointer.x,
      ey = pointer.y,
      xPoints,
      lines, keys = Object.keys(oCoords),
      j = keys.length - 1, i;
  this.__corner = 0;

  // cycle in reverse order so we pick first the one on top
  for (; j >= 0; j--) {
    i = keys[j];
    if (!this.isControlVisible(i)) {
      continue;
    }

    lines = this._getImageLines(forTouch ? oCoords[i].touchCorner : oCoords[i].corner);

    xPoints = this._findCrossPoints({ x: ex, y: ey }, lines);
    if (xPoints !== 0 && xPoints % 2 === 1) {
      this.__corner = i;
      return i;
    }
  }
  return false;
}
fabric.Object.prototype.extraFeatures = [];
fabric.Image.prototype.mirrorShadow = null;
fabric.Image.prototype.updateMirrorShadowData = function () {
  if (!Object.hasOwn(this, 'updateLayoutChanges')) return;
  if (!this.canvas || !this.canvas.instance_id) return;
  const preview = previews.find(preview => preview.id === this.canvas.instance_id );
  const key = 'mirror_shadows';
  const MIRROR_SHADOW_GROUP_ID_KEY = 'p_mirror_shadow_group_ids';
  const BACKGROUND_THEME_IMAGE_MIRROR_SHADOW_GROUP_ID_KEY = 'bk_img_mirror_shadow_group_ids';
  const ADD_BACKGROUND_THEME_IMAGE_MIRROR_SHADOW_GROUP_ID_KEY = 'add_bk_img_mirror_shadow_group_ids';
  
  if (this.id.startsWith(IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadowsByProduct = preview.data['p_'+ key] || [];
    shadowsByProduct[parseInt(index)] = this.mirrorShadow;
    
    const productImages = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    
    this.updateLayoutChanges(this.canvas.instance_id, 'p_'+ key, shadowsByProduct);
    this.updateLayoutChanges(this.canvas.instance_id, MIRROR_SHADOW_GROUP_ID_KEY, productImages.map(el => el.mirrorShadowGroupId));
  }
  
  if (this.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.mirrorShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));

    this.updateLayoutChanges(this.canvas.instance_id, 'bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, BACKGROUND_THEME_IMAGE_MIRROR_SHADOW_GROUP_ID_KEY, images.map(el => el.mirrorShadowGroupId));
  }
  
  if (this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['add_bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.mirrorShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    this.updateLayoutChanges(this.canvas.instance_id, 'add_bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, ADD_BACKGROUND_THEME_IMAGE_MIRROR_SHADOW_GROUP_ID_KEY, images.map(el => el.mirrorShadowGroupId));
  }
}
fabric.Image.prototype.ovalShadow = null;
fabric.Image.prototype.updateOvalShadowData = function () {
  if (!Object.hasOwn(this, 'updateLayoutChanges')) return;
  if (!this.canvas || !this.canvas.instance_id) return;
  const preview = previews.find(preview => preview.id === this.canvas.instance_id );
  const key = 'oval_shadows';
  const OVAL_SHADOW_GROUP_ID_KEY = 'p_oval_shadow_group_ids';
  const BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY = 'bk_img_oval_shadow_group_ids';
  const ADD_BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY = 'add_bk_img_oval_shadow_group_ids';
  
  if (this.id.startsWith(IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadowsByProduct = preview.data['p_'+ key] || [];
    shadowsByProduct[parseInt(index)] = this.ovalShadow;
    
    const productImages = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    
    this.updateLayoutChanges(this.canvas.instance_id, 'p_'+ key, shadowsByProduct);
    this.updateLayoutChanges(this.canvas.instance_id, OVAL_SHADOW_GROUP_ID_KEY, productImages.map(el => el.ovalShadowGroupId));
  }
  
  if (this.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.ovalShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));

    this.updateLayoutChanges(this.canvas.instance_id, 'bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY, images.map(el => el.ovalShadowGroupId));
  }
  
  if (this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['add_bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.ovalShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    this.updateLayoutChanges(this.canvas.instance_id, 'add_bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, ADD_BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY, images.map(el => el.ovalShadowGroupId));
  }
}
fabric.Image.prototype._render = (function(_render) {
  return function(ctx) {
    if (this.getSrc() && this.ovalShadow && this.ovalShadow.find(shadow => shadow.displayMode === BEHIND_DISPLAY_MODE)) {
      initOvalShadowBehind(this.ovalShadow.filter(shadow => shadow.displayMode === BEHIND_DISPLAY_MODE), this, ctx)
    }
    if (this.mirrorShadow && this.mirrorShadow.length > 0) {
      this.mirrorShadow.forEach((shadowData) => {
        if (shadowData && !shadowData.disabled) {
          const mockData = {
            [`${this.element_id}_mirror_position`]: 'bottom',
            [`${this.element_id}_mirror_fade`]: shadowData.fade
          };
          createMirror(this, ctx, mockData);
        }
      });
    }
    _render.call(this, ctx);
    if (this.getSrc() && this.ovalShadow && this.ovalShadow.find(shadow => shadow.displayMode === IN_FRONT_DISPLAY_MODE)) {
      initOvalShadowInFront(this.ovalShadow.filter(shadow => shadow.displayMode === IN_FRONT_DISPLAY_MODE), this, ctx)
    }
  };
})(fabric.Image.prototype._render);

fabric.Canvas.prototype.initialize = (function (originalFn) {
  return function (...args) {
    originalFn.call(this, ...args);

    const options = args[1];

    if (options?.resizeControl) {
      createCanvasResizeControl(this);
    }
  }
})(fabric.Canvas.prototype.initialize);

fabric.Canvas.prototype._checkTarget = function(pointer, obj, globalPointer) {
  if (
    obj &&
    obj.visible &&
    obj.evented &&
    obj.containsPoint(pointer)
  ) {
    if ((this.perPixelTargetFind || obj.perPixelTargetFind) && !obj.isEditing) {
      return this.isTargetTransparent(obj, globalPointer.x, globalPointer.y)
    }

    return obj.selectableBordersOnly ? !this.isInsideBorder(obj, pointer) : true;
  }
}

fabric.Canvas.prototype.isInsideBorder = function (target, pointer) {
  if (!target.strokeWidth) {
    return;
  }

  const pointerCoords = target.getLocalPointer(pointer);

  const strokeMargin = target.strokeWidth;
  const scaledWidth = target.getScaledWidth();
  const scaledHeight = target.getScaledHeight();

  return (
    pointerCoords.x > strokeMargin &&
    pointerCoords.x < scaledWidth - strokeMargin &&
    pointerCoords.y > strokeMargin &&
    pointerCoords.y < scaledHeight - strokeMargin
  );
}

const originalRender = fabric.Textbox.prototype._render;
const originalTextboxRender = fabric.Textbox.prototype.render;
const originalInitialize = fabric.Textbox.prototype.initialize;
const originalGetFontDeclaration = fabric.Textbox.prototype._getFontDeclaration;

const originalObjectRender = fabric.Object.prototype.render;
const originalObjectIsNotVisible = fabric.Object.prototype.isNotVisible;

const originalCanvasRenderAll = fabric.Canvas.prototype.renderAll;
const originalCanvasRequestRenderAll = fabric.Canvas.prototype.requestRenderAll;

// The editor also assigns isCurved directly. Track it in Fabric's dimension
// state so switching modes restores text height before rendering the cache.
fabric.Textbox.prototype._dimensionAffectingProps = fabric.Textbox.prototype._dimensionAffectingProps.concat('isCurved');
fabric.Textbox.prototype.cacheProperties = fabric.Textbox.prototype.cacheProperties.concat('isCurved', 'curvedDiameter', 'flipped');

const scaleCurvedTextFromSide = fabric.controlsUtils.wrapWithFireEvent('scaling',
  fabric.controlsUtils.wrapWithFixedAnchor((eventData, transform, x, y) => {
    const target = transform.target;
    if (target.lockScalingX || target.lockScalingY) return false;

    const point = fabric.controlsUtils.getLocalPoint(transform, transform.originX, transform.originY, x, y);
    const direction = transform.corner === 'ml' ? -1 : 1;
    if (point.x * direction <= 0) return false;

    const dimensions = target._getTransformedDimensions();
    const multiplier = transform.originX === 'center' ? 2 : 1;
    const scaleX = Math.abs(point.x) * multiplier * target.scaleX / dimensions.x;
    const scaleY = scaleX * transform.original.scaleY / transform.original.scaleX;
    if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY)) return false;

    const previousX = target.scaleX;
    const previousY = target.scaleY;
    target.set({ scaleX, scaleY });
    return target.scaleX !== previousX || target.scaleY !== previousY;
  })
);

// Textbox side handles normally change wrapping width. For a circular path,
// scale the whole object and use the same scaling events as the corner handles.
['ml', 'mr'].forEach(key => {
  const originalControl = fabric.Textbox.prototype.controls[key];
  fabric.Textbox.prototype.controls[key] = new fabric.Control({
    ...originalControl,
    getActionName(eventData, control, target) {
      return target.isCurved ? 'scale' : originalControl.getActionName(eventData, control, target);
    },
    actionHandler(eventData, transform, x, y) {
      return transform.target.isCurved
        ? scaleCurvedTextFromSide(eventData, transform, x, y)
        : originalControl.actionHandler(eventData, transform, x, y);
    },
  });
});

fabric.Textbox.prototype._getFontDeclaration = function (styleObject, forMeasuring) {
  const DEFAULT_FONT_NAME = 'Proxima-Nova-Semibold';
  const style = styleObject || this;
  const fontManager = window.fontFamilyManager;

  const styleFont = style.fontFamily;
  const textFont = this.fontFamily;

  const getAvailableFont = (font) => {
    if (!font) return null;

    if (!fontManager) {
      return font;
    }

    const fontObj = fontManager.findSimilarFontByIndex(font);
    if (fontObj?.index) return fontObj.index;

    // The picker can contain only a subset of the fonts declared by the page.
    // Keep an explicitly loaded font instead of silently replacing it.
    const loadedFont = Array.from(fabric.document.fonts || []).some(face =>
      face.status === 'loaded' && face.family.replace(/^['"]|['"]$/g, '') === font
    );
    return loadedFont ? font : null;
  };

  const family = getAvailableFont(styleFont)
    || getAvailableFont(textFont)
    || DEFAULT_FONT_NAME;

  const fontIsGeneric = fabric.Text.genericFonts.indexOf(family.toLowerCase()) > -1;

  const fontFamily =
    family.indexOf("'") > -1 ||
    family.indexOf(',') > -1 ||
    family.indexOf('"') > -1 ||
    fontIsGeneric
      ? family
      : `"${family}"`;

  return [
    fabric.isLikelyNode ? style.fontWeight : style.fontStyle,
    fabric.isLikelyNode ? style.fontStyle : style.fontWeight,
    forMeasuring ? this.CACHE_FONT_SIZE + 'px' : style.fontSize + 'px',
    fontFamily
  ].join(' ');
};


fabric.Textbox.prototype.generateResolvedStyleArray = function (preparedText) {
  const text = preparedText || this.text
  const lines = text.split('\n');
  const resolved = [];
  const styles = text ? this.styles || {} : {};
  let lastStyle = {
    fontSize: this.fontSize,
    fill: this.fontColor || this.fill,
    fontFamily: this.fontFamily,
  };
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const styleLine = [];
    const lineStyles = styles[lineIndex] || {};
    
    const isEmptyStyleObject = _.isEmpty(lineStyles) && !Array.isArray(lineStyles);
    
    if (isEmptyStyleObject || line.length === 0) {
      resolved[lineIndex] = [];
      continue;
    } 

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      let currentStyle = lineStyles[charIndex];

      if (!currentStyle) {
        currentStyle = { ...lastStyle };
      } else {
        currentStyle = { ...currentStyle };
      }

      styleLine[charIndex] = currentStyle;
      lastStyle = currentStyle;
    }

    resolved[lineIndex] = styleLine;
  }
  return resolved;
};
fabric.Textbox.prototype.getCircularText = function () {
  let text = this.text;

  const styles = Object.keys(this.styles).reduce((acc, rowKey, index) => {
    const row = this.styles[rowKey];
    const items = Object.keys(row).map(itemKey => row[itemKey]);
    if (index !== Object.keys(this.styles).length - 1) {
      items.push(items[items.length - 1]);
    }
    return acc.concat(items);
  }, []);

  let diameter = Number(this.curvedDiameter || 0);

  const fontSize = Number(this.fontSize);
  const kerning = (parseFloat(this.charSpacing || 0) / 1000) * fontSize; // <-- charSpacing in px

  const flipped = this.flipped || false;
  const fill = this.fill;
  let inwardFacing = true;
  let startAngle = 0;
  const canvas = fabric.util.createCanvasElement();
  const ctx = canvas.getContext('2d');
  let cw;
  let clockwise = -1;

  if (flipped) {
    startAngle = 180;
    inwardFacing = false;
  } else {
    styles.reverse();
  }

  startAngle *= Math.PI / 180;
  const textHeight = fontSize;

  const overflowY = Math.max(0, textHeight / 2 - diameter / 2);
  canvas.width = canvas.height = diameter + overflowY;
  ctx.font = this._getFontDeclaration();

  if (inwardFacing) {
    text = text.split('').reverse().join('');
  }

  ctx.translate(diameter / 2, diameter / 2);
  startAngle += (Math.PI * !inwardFacing);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  for (let x = 0; x < text.length; x++) {
    cw = ctx.measureText(text[x]).width;
    startAngle += ((cw + (x === text.length - 1 ? 0 : kerning)) / (diameter / 2 - textHeight)) / 2 * -clockwise;
  }

  ctx.rotate(startAngle);
  for (let x = 0; x < text.length; x++) {
    cw = ctx.measureText(text[x]).width;
    ctx.rotate((cw / 2) / (diameter / 2 - textHeight) * clockwise);
    const y = (inwardFacing ? 1 : -1) * (0 - diameter / 2 + textHeight / 2);
    if (this.strokeStyle && this.strokeWidth) {
      ctx.strokeStyle = this.strokeStyle;
      ctx.lineWidth = this.strokeWidth;
      ctx.miterLimit = 2;
      ctx.strokeText(text[x], 0, y);
    }
    ctx.fillStyle = styles[x]?.fill || fill;
    ctx.fillText(text[x], 0, y);

    ctx.rotate((cw / 2 + kerning) / (diameter / 2 - textHeight) * clockwise);
  }

  return canvas;
};

fabric.Textbox.prototype._trimCanvas = function(canvas) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  const w = canvas.width;
  const h = canvas.height;

  if (!w || !h) return null;

  const imageData = ctx.getImageData(0, 0, w, h).data;

  let minX = w, minY = h, maxX = 0, maxY = 0;
  let found = false;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const alpha = imageData[(y * w + x) * 4 + 3];

      if (alpha > 0) {
        found = true;

        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!found) return null;

  // +1 so a 1px tall/wide result does not become 0
  const cutW = Math.max(1, maxX - minX + 1);
  const cutH = Math.max(1, maxY - minY + 1);

  const cut = ctx.getImageData(minX, minY, cutW, cutH);

  canvas.width = cutW;
  canvas.height = cutH;

  const newCtx = canvas.getContext('2d');
  newCtx.putImageData(cut, 0, 0);

  return {
    left: minX,
    top: minY,
    width: cutW,
    height: cutH,
  };
};

// Resolve the bitmap bounds before Fabric chooses the object transform and cache
// size. Keep the wrapping width separate from the visible curved-text width.
fabric.Textbox.prototype._prepareCircularText = function () {
  if (this._straightTextWidth === undefined) {
    this._straightTextWidth = this.width;
  }

  const needsDimensions = this._shouldClearDimensionCache();
  if (this._circularText && !this.dirty && !needsDimensions &&
      this.width === this._circularText.canvas.width && this.height === this._circularText.canvas.height) {
    return;
  }

  if (needsDimensions) {
    this.width = this._straightTextWidth;
    this.initDimensions();
  }

  const canvas = this.getCircularText();
  const trim = this._trimCanvas(canvas);
  if (this.width !== canvas.width || this.height !== canvas.height) {
    this.set('dirty', true);
  }
  // Using set('width') would re-wrap the straight text and overwrite the height.
  this.width = canvas.width;
  this.height = canvas.height;
  this.saveState({ propertySet: '_dimensionAffectingProps' });
  this.setCoords();
  this._circularText = { canvas, trim };
};

// Keep native scaling during the drag, then bake the same factor into both
// the glyphs and their circular path without moving the rendered object.
fabric.Textbox.prototype.applyCurvedTextScale = function () {
  if (!this.isCurved || this.scaleY === 1) return;

  const scale = this.scaleY;
  if (!Number.isFinite(scale) || scale <= 0) return;
  const center = this.getCenterPoint();
  const styles = _.cloneDeep(this.styles);
  Object.values(styles || {}).forEach(line => {
    Object.values(line || {}).forEach(style => {
      if (style?.fontSize) style.fontSize = Number(style.fontSize) * scale;
    });
  });
  this._straightTextWidth = (this._straightTextWidth ?? this.width) * scale;
  this.set({
    fontSize: this.fontSize * scale,
    curvedDiameter: Number(this.curvedDiameter) * scale,
    styles,
    scaleX: this.scaleX / scale,
    scaleY: 1,
    dirty: true,
  });
  this._prepareCircularText();
  this.setPositionByOrigin(center, 'center', 'center');
  this.setCoords();
};

fabric.Textbox.prototype.initialize = function (text, options = {}) {
  originalInitialize.call(this, text, options);
  if (this.isCurved) {
    this.set('lockUniScaling', true);
    this._prepareCircularText();
  }
};

fabric.Textbox.prototype.render = function (ctx) {
  if (this.isCurved) {
    this.updateCurvedTextGeometry();
  } else if (this._straightTextWidth !== undefined) {
    this.width = this._straightTextWidth;
    delete this._straightTextWidth;
    delete this._circularText;
    delete this.curvedImageData;
    this.baseLineCoords = [];
    this.initDimensions();
    this.set('dirty', true);
    this.setCoords();
  }

  return originalTextboxRender.call(this, ctx);
};

fabric.Textbox.prototype._render = function (ctx) {
  if (!this.isCurved) {
    originalRender.call(this, ctx);
    return;
  }

  if (!this._circularText) {
    this._prepareCircularText();
  }
  const { canvas } = this._circularText;
  const drawX = -canvas.width / 2;
  const drawY = -canvas.height / 2;

  ctx.drawImage(canvas, drawX, drawY, canvas.width, canvas.height);
};

// Geometry must follow movement even when Fabric reuses the rendered bitmap.
// Export also calls this explicitly, including for objects inside cached groups.
fabric.Textbox.prototype.updateCurvedTextGeometry = function () {
  this._prepareCircularText();
  const { canvas, trim } = this._circularText;
  const drawX = -canvas.width / 2;
  const drawY = -canvas.height / 2;

  if (trim) {
    const localCircleCenterX = (Number(this.curvedDiameter || 0) / 2) - trim.left;
    const localCircleCenterY = (Number(this.curvedDiameter || 0) / 2) - trim.top;
    const localPathCenter = new fabric.Point(
      drawX + localCircleCenterX,
      drawY + localCircleCenterY,
    );
    const absolutePathCenter = fabric.util.transformPoint(localPathCenter, this.calcTransformMatrix());

    this.curvedImageData = {
      path: {
        centerX: absolutePathCenter.x,
        centerY: absolutePathCenter.y,
      },
      trim,
      diameter: Number(this.curvedDiameter || 0),
      renderedCanvas: {
        width: canvas.width,
        height: canvas.height,
      },
      object: {
        left: this.left,
        top: this.top,
        width: this.width,
        height: this.height,
        angle: this.angle || 0,
        align: this.textAlign,
        flipped: !!this.flipped,
      },
    };
  } else {
    delete this.curvedImageData;
  }

  this.baseLineCoords || (this.baseLineCoords = []);
  this.baseLineCoords[0] = {
    y: this.getCenterPoint().y + (this.flipped ? 0 : this.height / 2),
    x: (this.getScaledWidth() - canvas.width) / 2,
    object_id: this.id
  };
}

fabric.Object.prototype.render = function (ctx, ...props) {
  const effectsApplied = Effects.applyEffects(this, ctx, ...props);

  if (!effectsApplied) {
    return originalObjectRender.call(this, ctx, ...props);
  }
}

initSkewControls();
initProjectedShadowControls();
initWarpControls();

function flattenText(text) {
  const lines = text.split('\n');
  const result = [];
  lines.forEach((lineText, lineIndex) => {
    [...lineText].forEach((char, i) => {
      result.push({ char, line: lineIndex, indexInLine: i });
    });
  });
  return result;
}

function getStyleMapping(oldFlat, newFlat) {
  const m = oldFlat.length;
  const n = newFlat.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldFlat[i].char === newFlat[j].char) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const mapping = {};
  let i = 0, j = 0;

  while (i < m && j < n) {
    if (oldFlat[i].char === newFlat[j].char) {
      mapping[j] = i;
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  return mapping;
}

function transferCharStyles(oldText, newText, oldStyles) {
  const oldFlat = flattenText(oldText);
  const newFlat = flattenText(newText);
  const mapping = getStyleMapping(oldFlat, newFlat);

  const newStyles = {};

  newFlat.forEach(({ char, line, indexInLine }, flatIndex) => {
    const mappedOldIndex = mapping[flatIndex];
    if (mappedOldIndex !== undefined) {
      const { line: oldLine, indexInLine: oldIndex } = oldFlat[mappedOldIndex];
      const style = oldStyles?.[oldLine]?.[oldIndex];
      if (style) {
        if (!newStyles[line]) newStyles[line] = {};
        newStyles[line][indexInLine] = { ...style };
      }
    }
  });

  return newStyles;
}


fabric.IText.prototype.transferStylesFrom = function(oldText, oldStyles) {
  const newText = this.text;
  this.styles = transferCharStyles(oldText, newText, oldStyles);
};

fabric.IText.prototype.updateStylesFrom = function (newStyles) {
  const oldStyles = this.styles || {};
  const cleaned = {};

  // Step 1: Remove all old styles that are not present in newStyles
  for (const line in newStyles) {
    if (oldStyles[line]) {
      for (const char in newStyles[line]) {
        if (oldStyles[line] && oldStyles[line][char]) {
          if (!cleaned[line]) cleaned[line] = {};
          cleaned[line][char] = oldStyles[line][char];
        }
      }
    }
  }

  // Step 2: Add new styles that do not already exist
  for (const line in newStyles) {
    if (!cleaned[line]) cleaned[line] = {};
    for (const char in newStyles[line]) {
      if (!(char in cleaned[line])) {
        cleaned[line][char] = newStyles[line][char];
      }
    }
  }

  // Step 3: Apply the updated styles to the text object
  this.styles = cleaned;
  this.dirty = true;
  this.setCoords();
  this.canvas?.requestRenderAll();
};

fabric.Rect.prototype.toObject = (function(toObject) {
  return function(propertiesToInclude) {
    return fabric.util.object.extend(toObject.call(this, propertiesToInclude), {
      preserveCornerRadius: this.preserveCornerRadius,
    });
  };
})(fabric.Rect.prototype.toObject);

fabric.Rect.prototype._render = function (ctx) {

  // 1x1 case (used in spray brush) optimization was removed because
  // with caching and higher zoom level this makes more damage than help

  var 
    rx = this.rx ? Math.min(this.rx, this.width / 2) : 0,
    ry = this.ry ? Math.min(this.ry, this.height / 2) : 0,
    w = this.width,
    h = this.height,
    x = -this.width / 2,
    y = -this.height / 2,
    isRounded = rx !== 0 || ry !== 0,
    /* "magic number" for bezier approximations of arcs (http://itc.ktu.lt/itc354/Riskus354.pdf) */
    k = 1 - 0.5522847498;

  if (this.preserveCornerRadius) {
    rx /= this.scaleX;
    ry /= this.scaleY;
  }  
    
  ctx.beginPath();

  ctx.moveTo(x + rx, y);

  ctx.lineTo(x + w - rx, y);
  isRounded && ctx.bezierCurveTo(x + w - k * rx, y, x + w, y + k * ry, x + w, y + ry);

  ctx.lineTo(x + w, y + h - ry);
  isRounded && ctx.bezierCurveTo(x + w, y + h - k * ry, x + w - k * rx, y + h, x + w - rx, y + h);

  ctx.lineTo(x + rx, y + h);
  isRounded && ctx.bezierCurveTo(x + k * rx, y + h, x, y + h - k * ry, x, y + h - ry);

  ctx.lineTo(x, y + ry);
  isRounded && ctx.bezierCurveTo(x, y + k * ry, x + k * rx, y, x + rx, y);

  ctx.closePath();

  this._renderPaintInOrder(ctx);
};
fabric.IText.prototype.getShadowData = function () {
  const shadowCases = [
    'customOutline',
    'custom',
  ];
  let result = null;
  const {canvas} = this;
  const preview = window.previews.find(preview => preview.id === canvas.instance_id);
  const prefixId = this.element_id;
  for (const type of shadowCases) {
    switch (type) {
      case 'customOutline': {
        result = preview.data?.[`${prefixId}_custom_outline_shadow`];
        break;
      }
      case 'custom': {
        result = preview.data?.[`${prefixId}_custom_shadow`];
        break;
      }
    }
    if (result) return {type, value: result};
  }
  return null;
}

fabric.IText.prototype.setShadowData = function ({type, value}) {
  const { canvas, id } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;

  const key = id.split('_').pop();
  const prefixId = this.element_id;
  this.clearShadowData();
  switch (type) {
    case 'customOutline': {
      this.updateLayoutChanges(preview.id, `${prefixId}_custom_outline_shadow`, value);
      if ( this.id.startsWith(MULTI_LINE_PREFIX_ID)) {
        this.redrawMultiLineText(this.canvas.instance_id, this.element_id);
      }
      break;
    }
    case 'custom': {
      this.set('shadow', JSON.parse(value))
      this.canvas.requestRenderAll();
      this.updateLayoutChanges(preview.id, `${prefixId}_custom_shadow`, JSON.stringify(this.shadow))
      break;
    }
    default:
      return false;
  }

  return true;
}
fabric.IText.prototype.clearShadowData = function () {
  const { canvas } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;
  const prefixId = this.element_id;
  const outlineKey = `${prefixId}_custom_outline_shadow`;
  if (preview.data?.[outlineKey]) {
    delete preview.data[outlineKey];
    this.updateLayoutChanges(preview.id, outlineKey, null);
    if ( this.id.startsWith(MULTI_LINE_PREFIX_ID)) {
      this.redrawMultiLineText(this.canvas.instance_id, this.element_id);
    }
  }

  const customKey = `${prefixId}_custom_shadow`;
  if (preview.data?.[customKey]) {
    delete preview.data[customKey];
    this.set('shadow', null);
    this.updateLayoutChanges(preview.id, customKey, null);
    this.canvas.requestRenderAll();
  }
  return true;
}

const originalSetShadowData = fabric.Object.prototype._setShadow;
fabric.Object.prototype._setShadow = function (ctx) {
  if (ProjectedShadowEffect.isActive(this)) {
    ProjectedShadowEffect.apply(this, ctx);
  } else {
    originalSetShadowData.call(this, ctx);
  }
}

const originalCanvasAdd = fabric.Canvas.prototype.add;
const originalCanvasRemove = fabric.Canvas.prototype.remove;
fabric.Canvas.prototype.add = function () {
  if (!this._objectsMap) {
    this._objectsMap = new Map();
  }
  
  for (const argument of arguments) {
    if (!argument?.element_id) {
      continue;
    }
    
    this._objectsMap.set(argument.element_id, argument);
  } 
  
  return originalCanvasAdd.apply(this, arguments);
};
fabric.Canvas.prototype.remove = function () {
  if (this._objectsMap) {
    for (const argument of arguments) {
      if (!argument?.element_id) continue;
      this._objectsMap.delete(argument.element_id);
    }
  }

  return originalCanvasRemove.apply(this, arguments);
};
// outline shadow fix por-8438
const origRenderTextStroke = fabric.Text.prototype._renderTextStroke;

function shouldUseNonAccumulatingStroke(text) {
  return !!text.nonAccumulatingStroke;
}

function renderStrokeAsTintedBitmap(text, cs) {
  const strokeWidth = +text.strokeWidth || 0;
  if (!text.stroke || strokeWidth <= 0) return null;

  const saved = {
    fill: text.fill,
    stroke: text.stroke,
    strokeWidth: text.strokeWidth,
    shadow: text.shadow,
    paintFirst: text.paintFirst,
    globalCompositeOperation: null,
  };

  const pad = Math.ceil(strokeWidth * 2) + 4;
  const w = Math.max(1, Math.ceil((text.width || 0) + pad * 2));
  const h = Math.max(1, Math.ceil((text.height || 0) + pad * 2));

  const mask = fabric.util.createCanvasElement();
  mask.width = w;
  mask.height = h;
  const mctx = mask.getContext('2d');

  text.fill = 'rgba(0,0,0,0)';
  text.stroke = 'rgba(255,255,255,1)';
  text.strokeWidth = strokeWidth;
  text.shadow = null;
  text.paintFirst = 'stroke';

  mctx.clearRect(0, 0, w, h);

  mctx.save();
  mctx.translate(w / 2, h / 2);

  text._setLineDash(mctx, text.strokeDashArray);

  text._renderTextCommon(mctx, 'strokeText');

  mctx.restore();

  text.fill = saved.fill;
  text.stroke = saved.stroke;
  text.strokeWidth = saved.strokeWidth;
  text.shadow = saved.shadow;
  text.paintFirst = saved.paintFirst;

  const tinted = fabric.util.createCanvasElement();
  tinted.width = w;
  tinted.height = h;
  const tctx = tinted.getContext('2d');

  tctx.clearRect(0, 0, w, h);
  tctx.drawImage(mask, 0, 0);

  tctx.globalCompositeOperation = 'source-in';
  tctx.fillStyle = saved.stroke;
  tctx.fillRect(0, 0, w, h);

  return tinted;
}

fabric.Text.prototype._renderTextStroke = function (ctx) {
  if ((!this.stroke || this.strokeWidth === 0) && this.isEmptyStyles()) {
    return;
  }
  if (!this.stroke || !shouldUseNonAccumulatingStroke(this)) {
    return origRenderTextStroke.call(this, ctx);
  }

  if (this.shadow && !this.shadow.affectStroke) {
    this._removeShadow(ctx);
  }
  const tinted = renderStrokeAsTintedBitmap(this);
  if (!tinted) return;

  ctx.save();

  ctx.setLineDash([]);

  ctx.globalCompositeOperation = 'source-over';

  ctx.drawImage(tinted, -tinted.width / 2, -tinted.height / 2);

  ctx.restore();
};

Object.defineProperty(fabric.Object.prototype, 'getAbsoluteBoundingRect', {
  value: function () {
    const matrix = this.calcTransformMatrix();

    const w = this.width || 0;
    const h = this.height || 0;

    const points = [
      new fabric.Point(-w / 2, -h / 2),
      new fabric.Point( w / 2, -h / 2),
      new fabric.Point( w / 2,  h / 2),
      new fabric.Point(-w / 2,  h / 2),
    ].map(p => fabric.util.transformPoint(p, matrix));

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);

    const left = Math.min(...xs);
    const top = Math.min(...ys);
    const right = Math.max(...xs);
    const bottom = Math.max(...ys);

    return {
      left,
      top,
      width: right - left,
      height: bottom - top,
    }
  }
})

fabric.Object.prototype.isOnScreen = function(calculate) {
  if (!this.canvas) {
    return false;
  }
  
  const inverse = fabric.util.invertTransform(
    this.canvas.viewportTransform
  );

  const pointTL =
    this.canvas.vptCoords?.tl ??
    fabric.util.transformPoint(
      new fabric.Point(0, 0),
      inverse
    );
  const pointBR =
    this.canvas.vptCoords?.br ??
    fabric.util.transformPoint(
      new fabric.Point(this.canvas.getWidth(), this.canvas.getHeight()),
      inverse
    );
  const points = this.getCoords(true, calculate);
  // if some point is on screen, the object is on screen.
  if (points.some(function(point) {
    return point.x <= pointBR.x && point.x >= pointTL.x &&
      point.y <= pointBR.y && point.y >= pointTL.y;
  })) {
    return true;
  }
  // no points on screen, check intersection with absolute coordinates
  if (this.intersectsWithRect(pointTL, pointBR, true, calculate)) {
    return true;
  }
  return this._containsCenterOfCanvas(pointTL, pointBR, calculate);
};

fabric.Object.prototype.isNotVisible = function () {
  const target = this.smartObject || this;

  return originalObjectIsNotVisible.call(target);
};

fabric.Canvas.prototype.renderLock = new RenderLock();

fabric.Canvas.prototype.renderAll = function (...args) {
  if (this.renderLock.isLocked()) {
    return this;
  }

  return originalCanvasRenderAll.call(this, ...args);
}

fabric.Canvas.prototype.requestRenderAll = function (...args) {
  if (this.renderLock.isLocked()) {
    return this;
  }
  
  return originalCanvasRequestRenderAll.call(this, args);
}
